import { useQueries } from "@tanstack/react-query";
import { Ikon, Iskelet, cn } from "@koclukpanel/ui";
import { ApiHatasi, mesajKutum } from "./api";
import { KAYNAKLAR, type Kaynak } from "./YoneticiBugun";

/**
 * BEKLEYENLER — UI/UX Dalga 2 (M3)
 *
 * ================================================================
 * NEDEN: BEKLEYEN İŞ BEŞ AYRI YERDE, HEPSİ AYRI KART
 * ================================================================
 * Koçun "Bugün" ekranında onay, talep, izin ve mesaj bekleyenleri ya hiç
 * görünmüyordu (mesaj) ya da her biri 120 px'lik ayrı bir sayı kartıydı ve
 * iş listesinin altında kalıyordu. Tek satırlık bir kutu hepsini söylüyor:
 * hangi kuyrukta kaç şey bekliyor, dokununca ilgili alana gidiyor.
 *
 * ================================================================
 * KURALLAR `YoneticiBugun` İLE AYNI — KAYNAKLAR PAYLAŞILIYOR
 * ================================================================
 * Kaynak listesi `YoneticiBugun.KAYNAKLAR`dan geliyor (ikinci bir liste
 * eskirdi) ve sorgu anahtarları aynı (`["bugun", anahtar]`): iki bileşen
 * birlikte çizilirse react-query ikinci istek atmıyor.
 *  - İzni olmayan kaynak HİÇ sorulmuyor (her ret denetim kaydına düşüyor).
 *  - 403/404 "bu senin işin değil": sessizce atlanır, "temiz" denmez.
 *  - Diğer hata "kontrol edilemedi": kesikli çip — sayı değil, boşluk.
 *  - Hepsi sıfırsa tek cümle: bakıldı ve temiz.
 */

/** Okunmamış mesaj: kuyruk değil ama karşı tarafın BEKLEDİĞİ tek şey. */
const MESAJ_KAYNAGI: Kaynak = {
  anahtar: "mesaj-okunmamis",
  ikon: "gorusme",
  ekran: "mesajlar",
  onem: "dikkat",
  etiket: "okunmamış mesaj",
  izin: "mesaj.yazisma.goruntule",
  getir: async () => (await mesajKutum()).toplamOkunmamis,
};

const ONEM_SIRASI = { acil: 0, dikkat: 1, bilgi: 2 } as const;

export function Bekleyenler({
  onEkranSec,
  izinler,
}: {
  onEkranSec: (ekran: string) => void;
  izinler: Record<string, string>;
}) {
  // Süzme burada (enabled ile değil): dizin kayması yanlış çipe yanlış sayı yazdırırdı.
  const sorulacak = [...KAYNAKLAR, MESAJ_KAYNAGI].filter((k) =>
    typeof k.izin === "string" ? k.izin in izinler : k.izin.some((kod) => kod in izinler),
  );

  const sorgular = useQueries({
    queries: sorulacak.map((k) => ({
      queryKey: ["bugun", k.anahtar],
      queryFn: k.getir,
      retry: false,
      staleTime: 30_000,
    })),
  });

  if (sorulacak.length === 0) return null;
  if (sorgular.some((s) => s.isPending)) return <Iskelet satir={1} />;

  const ciplar: Array<{ kaynak: Kaynak; sayi: number | null }> = [];
  for (const [i, s] of sorgular.entries()) {
    const kaynak = sorulacak[i]!;
    if (s.isError) {
      const yetkiYok =
        s.error instanceof ApiHatasi && (s.error.durum === 403 || s.error.durum === 404);
      if (!yetkiYok) ciplar.push({ kaynak, sayi: null });
      continue;
    }
    if ((s.data ?? 0) > 0) ciplar.push({ kaynak, sayi: s.data ?? 0 });
  }
  // Hiçbir kaynak yanıtlanamadıysa (hepsi 403/404) susuyoruz: "temiz" demek yalan olurdu.
  const yanitlanan = sorgular.filter(
    (s) => !(s.isError && s.error instanceof ApiHatasi && [403, 404].includes(s.error.durum)),
  );
  if (yanitlanan.length === 0) return null;

  ciplar.sort((a, b) => {
    // Ölçülemeyen en önde: bilinmeyen kuyruk bilinen işten önce görülmeli.
    if ((a.sayi === null) !== (b.sayi === null)) return a.sayi === null ? -1 : 1;
    return ONEM_SIRASI[a.kaynak.onem] - ONEM_SIRASI[b.kaynak.onem];
  });

  return (
    <section aria-label="Bekleyenler" className="mb-4 flex flex-wrap items-center gap-2">
      <h2 className="text-notr-900 text-[15px] font-semibold">Bekleyenler</h2>
      {ciplar.length === 0 ? (
        <p className="text-onay-700 inline-flex items-center gap-1 text-[13px] font-medium">
          <Ikon ad="tamam" boyut="kucuk" />
          Bekleyen iş yok
        </p>
      ) : (
        ciplar.map(({ kaynak, sayi }) => (
          <button
            key={kaynak.anahtar}
            type="button"
            onClick={() => onEkranSec(kaynak.ekran)}
            className={cn(
              "rounded-rozet min-h-dokunma inline-flex items-center gap-1.5 px-3 text-[13px] ring-1 ring-inset",
              "ease-cikis hover:bg-notr-50 transition-colors duration-(--sure-anlik)",
              sayi === null
                ? "text-notr-600 ring-notr-300 bg-notr-0 border-notr-400 border-dashed"
                : kaynak.onem === "acil"
                  ? "bg-hata-50 text-hata-700 ring-hata-200"
                  : kaynak.onem === "dikkat"
                    ? "bg-uyari-50 text-uyari-800 ring-uyari-200"
                    : "bg-notr-0 text-notr-700 ring-notr-200",
            )}
          >
            <Ikon ad={sayi === null ? "olculemedi" : kaynak.ikon} boyut="kucuk" />
            {sayi === null ? (
              <span>{kaynak.etiket} — kontrol edilemedi</span>
            ) : (
              <span>
                <strong className="font-semibold">{sayi}</strong> {kaynak.etiket}
              </span>
            )}
          </button>
        ))
      )}
    </section>
  );
}
